import { component } from '@smoovy/component';
import { listenCompose, listenEl, Unlisten } from '@smoovy/event';
import { Observable, observe, unobserve } from '@smoovy/observer';
import { Ticker } from '@smoovy/ticker';
import { Coordinate, lerp } from '@smoovy/utils';

import svcs from '../services';
import { isDesktop } from '../utils';

export enum CursorEvent {
  RESET = 'reset',
  UPDATE_TEXT = 'update-text',
  REPLACE_TEXT = 'replace-text',
}

interface CursorState {
  id: number;
  text: string;
  unshift?: boolean;
}

@component({
  selector: '.cursor',
  condition: () => isDesktop()
})
export class Cursor {
  private unlisten?: Unlisten;
  private observable: Observable<HTMLElement>;
  private textElement: HTMLElement;
  private ticker = new Ticker(Infinity);
  private mousePosition: Coordinate = { x: 0, y: 0 };
  private mouseCursorPosition: Coordinate = { x: 0, y: 0 };
  private cursorPosition: Coordinate = { x: 0, y: 0 };
  private states: CursorState[] = [];
  private ready = false;
  private radius = 0;

  constructor(
    protected element: HTMLElement
  ) {
    this.init();

    this.observable = observe(this.element, { observeResize: true });
    this.textElement = element.querySelector('.cursor-text')!;
  }

  private async init() {
    const service = await svcs.cursor.service;
    const viewport = await svcs.viewport.service;

    this.unlisten = listenCompose(
      listenEl(window, 'mousemove', (event) => this.handleMove(event)),
      service.on(CursorEvent.REPLACE_TEXT, (state: CursorState) => {
        const index = this.states.findIndex(s => s.id === state.id)

        if (index > -1) {
          this.states.splice(index, 1, state);
        }

        this.update();
      }),
      viewport.onUpdate(() => this.updateRadius()),
      this.observable.onUpdate(() => this.updateRadius()),
      service.on(CursorEvent.UPDATE_TEXT, (state: CursorState) => {
        if (state.text) {
          if (state.unshift) {
            this.states.unshift(state);
          } else {
            this.states.push(state);
          }
        } else {
          const index = this.states.findIndex(s => s.id === state.id)

          if (index > -1) {
            this.states.splice(index, 1);
          }
        }

        this.update();
      }),
      service.on(CursorEvent.RESET, () => {
        this.states = [];

        this.update();
      })
    );

    this.ticker.add((delta) => this.animate(delta));
    setTimeout(() => this.updateRadius());
  }

  private updateRadius() {
    this.radius = this.observable.offset.width * 1.5625;
  }

  private update() {
    const state = this.states[this.states.length - 1];

    this.element.classList.toggle('is-active', !!state);

    if (state) {
      this.textElement.textContent = state.text;
    }
  }

  private handleMove(event: MouseEvent) {
    this.mousePosition.x = event.clientX;
    this.mousePosition.y = event.clientY;

    const deltaY = this.mouseCursorPosition.y - this.mousePosition.y;
    const deltaX = this.mouseCursorPosition.x - this.mousePosition.x;
    const theta = Math.atan2(deltaY, deltaX) + Math.PI;
    const paddingX = this.radius * Math.cos(theta);
    const paddingY = this.radius * Math.sin(theta);

    this.mouseCursorPosition.x = this.mousePosition.x - paddingX;
    this.mouseCursorPosition.y = this.mousePosition.y - paddingY;

    if ( ! this.ready) {
      setTimeout(() => this.element.classList.add('is-ready'), 500);
    }
  }

  private animate(delta = 1) {
    const cursorPos = this.cursorPosition
    const mouseCursorPos = this.mouseCursorPosition;
    const speed = Math.max(
      Math.abs(mouseCursorPos.x - cursorPos.x) * 0.001,
      Math.abs(mouseCursorPos.y - cursorPos.y) * 0.001,
    );

    cursorPos.x = lerp(cursorPos.x, mouseCursorPos.x, 0.12 * delta);
    cursorPos.y = lerp(cursorPos.y, mouseCursorPos.y, 0.12 * delta);

    this.element.style.transform = `
      translate3d(${cursorPos.x.toFixed(4)}px, ${cursorPos.y.toFixed(4)}px, 0)
      scale(${1 - Math.min(speed, .5)})
    `;
  }

  public onDestroy() {
    unobserve(this.observable);

    if (this.unlisten) {
      this.unlisten();
      delete this.unlisten;
    }
  }
}